/**
 * Which product app you are in, decided by the hostname.
 *
 * WHY THE APP HAS SHELLS AT ALL. Talking to customers produced a negative
 * signal, not a neutral one: people who came for Brainstorms glaze over at page
 * building, link maps and deployment, and people who came for News Beats
 * explicitly do not want the SEO pipeline. Showing everything to either of them
 * is not merely cluttered — it costs the sale. So each product is served at its
 * own host and shows only its own work:
 *
 *     app.lumnian.com          the house: launcher, account, settings, the shelf
 *     recon.lumnian.com        Lumnian Recon (warroom. and boardroom. still open it)
 *     beats.lumnian.com        News Beats
 *     publish.lumnian.com      subjects -> keywords -> pages -> deploy
 *
 * ONE BUNDLE SERVES ALL FOUR. They are not separate builds: these are four
 * authenticated React SPAs with identical needs, so three more deployables would
 * buy nothing a user can see while costing a shared-package extraction paid
 * forever. (The reader, `frontend/reader`, IS a separate app — it is
 * unauthenticated, server-rendered and a different framework. That is what
 * earning your own deployable looks like.) Extraction stays cheap later, if one
 * of these outgrows the house.
 *
 * THE FIRST LABEL IS THE WHOLE RULE, and it is deliberately domain-agnostic:
 * `recon.lumnian.test` (local, in /etc/hosts) and `recon.lumnian.com`
 * (production) both resolve to the same shell with no configuration and no
 * environment branching. There is no `?shell=` override on purpose — a
 * dev-only mechanism would drift from the real one and become the thing that is
 * actually tested.
 *
 * Dependency-free so CRA's jest can test it: react-router-dom v7 does not
 * resolve under this jest config, so anything a routed component imports has to
 * live apart to be testable. Same reason `pages/deliverTabs.ts` and
 * `pages/trendFreshness.ts` exist.
 */
import type { Product } from '../types';

export type Shell = 'app' | 'beats' | 'publish' | 'boardroom';

export const SHELLS: Record<
  Shell,
  {
    /** The wordmark. What a person tells a colleague they are using. */
    name: string;
    /** The product this shell sells. `app` is the house, and sells nothing. */
    product: Product | null;
    /** Where the wordmark links, and where a bare visit lands. */
    home: string;
    /** The first label of its address: `recon` in recon.lumnian.com. */
    host: string;
    /** The name inside a sentence: "back to Recon", not "back to Lumnian Recon". */
    short: string;
  }
> = {
  app: { name: 'Lumnian', product: null, home: '/', host: 'app', short: 'Lumnian' },
  beats: { name: 'Lumnian News Beats', product: 'BEATS', home: '/news-beats', host: 'beats', short: 'News Beats' },
  publish: { name: 'Lumnian Publish', product: 'PUBLISH', home: '/subjects', host: 'publish', short: 'Publish' },
  // The market-research product. It took its own shell while it was being
  // compared against the brainstorm room it has since replaced. Its address
  // followed its name (Boardroom, War Room, now Lumnian Recon); the code
  // still calls it boardroom.
  boardroom: { name: 'Lumnian Recon', product: 'BOARDROOM', home: '/boardroom', host: 'recon', short: 'Recon' },
};

/** Old addresses that still open their shell, so a link or a bookmark from
 *  before a rename keeps working. */
const OLD_HOSTS: Record<string, Shell> = { boardroom: 'boardroom', warroom: 'boardroom' };

const BY_HOST: Record<string, Shell> = {
  ...OLD_HOSTS,
  ...Object.fromEntries((Object.keys(SHELLS) as Shell[]).map(s => [SHELLS[s].host, s])),
};

/**
 * The shell for a hostname.
 *
 * Falls back to `app` for anything unrecognised — a bare `localhost`, an IP, a
 * `www.`, a preview host, a typo. Falling back rather than throwing is the same
 * argument `resolveDeliverTab` makes: landing in the house is a coherent place
 * to be, and rendering nothing is not.
 */
export function shellFromHost(hostname: string): Shell {
  const first = (hostname || '').toLowerCase().split('.')[0];
  return BY_HOST[first] ?? 'app';
}

/** The product a shell sells, or null in the house. */
export function productOfShell(shell: Shell): Product | null {
  return SHELLS[shell].product;
}
